import { useState, useMemo, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useOrderFooterInset } from '../hooks/useOrderFooterInset';
import {
  getWholesaleQtyError,
  WHOLESALE_CATEGORY_ORDER,
} from '../utils/wholesaleOrderForm';
import { WHOLESALE_MIN_TOTAL_QTY } from '../utils/shopOrderType';
import { getWholesaleLinesTotalQty } from '../utils/shopWholesaleBasket';
import ModelPicker from './ModelPicker';
import OrderFormStickyBar from './OrderFormStickyBar';
import WholesaleModelSidePanel from './WholesaleModelSidePanel';
import WholesaleOrderBucketSheet from './WholesaleOrderBucketSheet';
import ShopWholesaleMinQtyCallout from './ShopWholesaleMinQtyCallout';

const ShopWholesaleBasketForm = ({
  inventory = [],
  t,
  onProceedToCheckout,
  loading = false,
}) => {
  const [panelModel, setPanelModel] = useState(null);
  const [bucketSheetOpen, setBucketSheetOpen] = useState(false);
  const [selectedLines, setSelectedLines] = useState({});
  const formRef = useRef(null);
  const footerRef = useRef(null);

  useOrderFooterInset(formRef, footerRef);

  const linesArray = useMemo(() => Object.values(selectedLines), [selectedLines]);
  const selectedModelIds = useMemo(() => Object.keys(selectedLines), [selectedLines]);
  const selectedQtyById = useMemo(
    () => Object.fromEntries(linesArray.map((line) => [line.model, line.qty])),
    [linesArray]
  );

  const totalQty = getWholesaleLinesTotalQty(linesArray);
  const meetsMinQty = totalQty >= WHOLESALE_MIN_TOTAL_QTY;
  const hasQtyErrors = linesArray.some((item) => getWholesaleQtyError(item));

  const grandTotal = useMemo(
    () => linesArray.reduce((sum, line) => sum + (line.totalValue || 0), 0),
    [linesArray]
  );
  const totalAdvance = useMemo(
    () => linesArray.reduce((sum, line) => sum + (line.advance || 0), 0),
    [linesArray]
  );
  const balance = grandTotal - totalAdvance;
  const minAdvanceRequired = Math.ceil(grandTotal * 0.4);

  const groupedLines = useMemo(() => {
    const groups = {};
    for (const line of linesArray) {
      const cat = line.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(line);
    }
    return WHOLESALE_CATEGORY_ORDER.filter((cat) => groups[cat]?.length)
      .map((cat) => ({ category: cat, lines: groups[cat] }))
      .concat(
        groups.other?.length
          ? [{ category: 'other', label: 'Other', lines: groups.other }]
          : []
      );
  }, [linesArray]);

  const handleModelClick = (inv) => {
    setBucketSheetOpen(false);
    setPanelModel(inv);
  };

  const handleAddToBucket = (line) => {
    const isUpdate = Boolean(selectedLines[line.model]);
    setSelectedLines((prev) => ({
      ...prev,
      [line.model]: line,
    }));
    setPanelModel(null);
    toast.success(
      isUpdate ? t('wholesaleBasket.updatedInBucket') : t('wholesaleBasket.addedToBucket')
    );
  };

  const handleRemoveFromBucket = (modelId) => {
    setSelectedLines((prev) => {
      const next = { ...prev };
      delete next[modelId];
      return next;
    });
  };

  const handleEditLine = (modelId) => {
    const inv = inventory.find((item) => item._id === modelId);
    if (inv) {
      setBucketSheetOpen(false);
      setPanelModel(inv);
    }
  };

  const resetBucket = useCallback(() => {
    setSelectedLines({});
    setPanelModel(null);
    setBucketSheetOpen(false);
  }, []);

  const handleClearForm = () => {
    resetBucket();
    toast.success(t('wholesaleBasket.cleared'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (linesArray.length === 0) {
      toast.error(t('wholesaleBasket.emptyBucket'));
      return;
    }
    for (const line of linesArray) {
      const qtyError = getWholesaleQtyError(line);
      if (qtyError) {
        toast.error(`${line.modelName}: ${qtyError}`);
        return;
      }
    }
    if (!meetsMinQty) {
      toast.error(t('toast.wholesaleMinQty', { count: WHOLESALE_MIN_TOTAL_QTY }));
      return;
    }
    onProceedToCheckout(linesArray);
  };

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <ShopWholesaleMinQtyCallout
        t={t}
        current={linesArray.length > 0 ? totalQty : undefined}
        showProgress
        className="mb-2 shrink-0"
      />

      <form
        ref={formRef}
        id="shop-wholesale-basket-form"
        onSubmit={handleSubmit}
        className="order-form--mobile-overlay-footer flex flex-1 min-h-0 flex-col rounded-xl border border-border bg-card p-2 shadow-sm sm:p-4"
      >
        <section className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <div className="hidden shrink-0 items-center justify-between gap-2 sm:flex">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">
              {t('wholesaleBasket.selectModels')}
            </h2>
            <span className="text-right text-xs text-muted-foreground">
              {linesArray.length > 0
                ? t('wholesaleBasket.inBucket', { count: linesArray.length })
                : t('wholesaleBasket.tapToAdd')}
            </span>
          </div>
          <ModelPicker
            inventory={inventory}
            multiSelect
            fillHeight
            compactMobile
            dynamicScrollInset
            selectedModelIds={selectedModelIds}
            selectedQtyById={selectedQtyById}
            onToggleSelect={handleModelClick}
            accentRing="ring-primary"
            outOfStockLabel={t('gallery.stockOutOfStock')}
            hidePrice
          />
        </section>

        <OrderFormStickyBar
          ref={footerRef}
          variant="inCard"
          grandTotal={grandTotal}
          totalAdvance={totalAdvance}
          balance={balance}
          loading={loading}
          submitDisabled={hasQtyErrors || linesArray.length === 0 || !meetsMinQty}
          submitLabel={t('wholesaleBasket.submit')}
          submitClassName="rounded-full bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground hover:bg-accent/90 disabled:opacity-50"
          minAdvanceRequired={minAdvanceRequired}
          isAdvanceSufficient
          showAdvancePolicy={false}
          showOrderTotals={false}
          onClearForm={handleClearForm}
          clearLabel={t('wholesaleBasket.clear')}
          onViewBucket={() => setBucketSheetOpen(true)}
          bucketItemCount={linesArray.length}
          viewBucketLabel={t('wholesaleBasket.viewBucket')}
          viewBucketShortLabel={t('wholesaleBasket.viewBucketShort')}
        />
      </form>

      <WholesaleModelSidePanel
        item={panelModel}
        existingLine={panelModel ? selectedLines[panelModel._id] || null : null}
        open={Boolean(panelModel)}
        onClose={() => setPanelModel(null)}
        onAddToBucket={handleAddToBucket}
        onRemoveFromBucket={() => handleRemoveFromBucket(panelModel._id)}
        hidePrice
        t={t}
      />

      <WholesaleOrderBucketSheet
        open={bucketSheetOpen}
        onClose={() => setBucketSheetOpen(false)}
        groupedLines={groupedLines}
        itemCount={linesArray.length}
        formatMoney={formatMoney}
        onEditLine={handleEditLine}
        onRemoveLine={handleRemoveFromBucket}
        grandTotal={grandTotal}
        totalAdvance={totalAdvance}
        balance={balance}
        hidePrice
        showOrderTotals={meetsMinQty}
      />
    </div>
  );
};

export default ShopWholesaleBasketForm;
